import React, { useState, useEffect, useContext } from "react";
import "./Payments-Table.scss";

//import components
import PaymentConfirmationModal from "./ApprovePaymentModal";

//icons import
import { FaPercentage } from "react-icons/fa";
import { MdCreditScore } from "react-icons/md";

//import context
import { MyContext } from "../../../../tenantsDashContext";

//redux imports
import { useSelector, useDispatch } from "react-redux";
import {
  fetchPayments,
  confirmPayments,
  reversePayment,
} from "../../../../redux/slices/sliceActions";

// --- Added helper to safely produce ISO strings from possibly invalid dates ---
const safeISO = (value) => {
	// returns an ISO string or null if invalid
	if (!value) return null;
	const d = new Date(value);
	return isNaN(d.getTime()) ? null : d.toISOString();
};

const getISO = (primary, fallback) => {
	return safeISO(primary) || safeISO(fallback) || new Date().toISOString();
};
// --- end helper ---

const TransactionsTable = ({
  approved,
  awaitingApproval,
  selectedApartmentId,
  selectedMonth,
  selectedYear,
  monthChoices,
  currentProperty, // Add this new prop
}) => {
  // redux methods
  const dispatch = useDispatch();
  const { payments, properties } = useSelector((state) => state.landlordslice);

  useEffect(() => {
    console.log("payments", payments);

    // If currentProperty is provided, use its payments
    if (currentProperty && currentProperty.payments) {
      console.log("payments houses", currentProperty.payments);
      const formattedPayments = currentProperty.payments.map((payment) => {
        return {
          payment_id_to_display: `PAY${payment._id?.slice(-3)}`,
          house_no: payment?.invoice?.tenant?.house?.house_no,
          payment_id: payment._id,
          payment_status: payment.status === "pending" ? 0 : 1,
          approval_status: payment?.isVerified == true ? 1 : 0,
          created_at: safeISO(payment.createdAt) || "",
          payment_date: getISO(payment.payment_date, payment.createdAt),
          invoice: `INV${payment._id?.slice(-3)}`,
          rent_amount: payment.rent || 0,
          water_bill_amount: payment?.water || 0,
          garbage_charge_amount: payment?.garbage || 0,
          service_fee_amount: payment.service_charge || 0,
          unpaid_deposit: payment?.deposit || 0,
          amount: payment.amount,
          arrears_amount: payment?.arrears || 0,
          late_fee: payment?.late_fee,
          narration: payment?.narration || "",
          reference: payment?.reference || "",
        };
      });
      setPaymentsReceived(formattedPayments);
      return;
    }

    // Existing logic for when currentProperty is not provided
    if (payments.length > 0) {
      //format the payments
      const formattedPayments = payments.map((payment) => {
        return {
          payment_id_to_display: `PAY${payment._id?.slice(-3)}`,
          house_no: payment?.tenant?.house?.house_no,
          payment_id: payment._id,
          payment_status: payment.status === "pending" ? 0 : 1,
          approval_status: payment?.isVerified == true ? 1 : 0,
          created_at: safeISO(payment.createdAt) || "",
          payment_date: getISO(payment.payment_date, payment.createdAt),
          invoice: `INV${payment._id?.slice(-3)}`,
          rent_amount: payment.rent || 0,
          water_bill_amount: payment?.water || 0,
          garbage_charge_amount: payment?.garbage || 0,
          service_fee_amount: payment.service_charge || 0,
          unpaid_deposit: payment?.deposit || 0,
          amount: payment.amount,
          arrears_amount: payment?.arrears || 0,
          late_fee: payment?.late_fee,
          narration: payment?.narration || "",
          reference: payment?.reference || "",
        };
      });
      setPaymentsReceived(formattedPayments);
    } else {
      const formattedPayments = properties?.[0]?.payments.map((payment) => {
        return {
          payment_id_to_display: `PAY${payment._id?.slice(-3)}`,
          house_no: payment?.tenant?.house?.house_no,
          payment_id: payment._id,
          payment_status: payment.status === "pending" ? 0 : 1,
          approval_status: payment?.isVerified == true ? 1 : 0,
          created_at: safeISO(payment.createdAt) || "",
          payment_date: getISO(payment.payment_date, payment.createdAt),
          invoice: `INV${payment._id?.slice(-3)}`,
          rent_amount: payment.rent || 0,
          water_bill_amount: payment?.water || 0,
          garbage_charge_amount: payment?.garbage || 0,
          service_fee_amount: payment.service_charge || 0,
          unpaid_deposit: payment?.deposit || 0,
          amount: payment.amount,
          arrears_amount: payment?.arrears || 0,
          late_fee: payment?.late_fee,
          narration: payment?.narration || "",
          reference: payment?.reference || "",
        };
      });

      setPaymentsReceived(formattedPayments);
    }
  }, [payments, currentProperty]);
  //check shared value from context
  const { sharedValue, setSharedValue } = useContext(MyContext);

  const [paymentsReceived, setPaymentsReceived] = useState([]);
  const [visiblePayments, setVisiblePayments] = useState(3); // State to manage visible payments
  const [expandedPayment, setExpandedPayment] = useState(null); // State to track which payment is expanded

  const [isFiltered, setIsFiltered] = useState(false);
  useEffect(() => {
    if (isFiltered) {
      return;
    }

    // Determine the payment source based on currentProperty
    const paymentSource =
      currentProperty && currentProperty.payments
        ? currentProperty.payments
        : payments;

    if (awaitingApproval) {
      console.log("approved", approved, "awaiting approval", awaitingApproval);
      // Filter payments that are not approved (status !== 1)
      const pendingPayments = paymentSource.filter(
        (payment) => payment.isVerified == false
      );
      setPaymentsReceived(
        pendingPayments.map((payment) => ({
          payment_id_to_display: `PAY${payment._id?.slice(-3)}`,
          house_no: payment?.tenant?.house?.house_no,
          payment_id: payment._id,
          payment_status: payment.status === "pending" ? 0 : 1,
          approval_status: payment?.isVerified == true ? 1 : 0,
          created_at: new Date(payment.createdAt).toISOString(),
          payment_date:
            new Date(payment.payment_date).toISOString() ||
            new Date(payment.createdAt).toISOString(),
          invoice: `INV${payment._id?.slice(-3)}`,
          rent_amount: payment.rent || 0,
          water_bill_amount: payment?.water || 0,
          garbage_charge_amount: payment?.garbage || 0,
          service_fee_amount: payment.service_charge || 0,
          unpaid_deposit: payment?.deposit || 0,
          amount: payment.amount,
          arrears_amount: payment?.arrears || 0,
          late_fee: payment?.late_fee,
          narration: payment?.narration || "",
          reference: payment?.reference || "",
        }))
      );
      setIsFiltered(true);
      console.log("pending payments", pendingPayments);
      return;
    }

    if (approved) {
      console.log("approved", approved, "awaiting approval", awaitingApproval);
      // Filter payments that are not approved (status !== 1)
      const pendingPayments = paymentSource.filter(
        (payment) => payment.isVerified == true
      );
      setPaymentsReceived(
        pendingPayments.map((payment) => ({
          payment_id_to_display: `PAY${payment._id?.slice(-3)}`,
          house_no: payment?.tenant?.house?.house_no,
          payment_id: payment._id,
          payment_status: payment.status === "pending" ? 0 : 1,
          approval_status: payment?.isVerified == true ? 1 : 0,
          created_at: new Date(payment.createdAt).toISOString(),
          payment_date:
            new Date(payment.payment_date).toISOString() ||
            new Date(payment.createdAt).toISOString(),
          invoice: `INV${payment._id?.slice(-3)}`,
          rent_amount: payment.rent || 0,
          water_bill_amount: payment?.water || 0,
          garbage_charge_amount: payment?.garbage || 0,
          service_fee_amount: payment.service_charge || 0,
          unpaid_deposit: payment?.deposit || 0,
          amount: payment.amount,
          arrears_amount: payment?.arrears || 0,
          late_fee: payment?.late_fee,
          narration: payment?.narration || "",
          reference: payment?.reference || "",
        }))
      );
      setIsFiltered(true);
      console.log("pending payments", pendingPayments);
      return;
    }
  }, [awaitingApproval, approved, paymentsReceived, currentProperty]);

  const paymentData = {
    payments: paymentsReceived,
  };

  if (sharedValue === "Invoices") {
    paymentData.payments = paymentsReceived.filter(
      (payment) => payment.payment_status === 0
    );
  }

  // Function to show more payments
  const showMorePayments = () => {
    setVisiblePayments((prev) =>
      Math.min(prev + 3, paymentData.payments.length)
    );
  };

  // Function to hide all payments except the first three
  const hideAllPayments = () => {
    setVisiblePayments(3);
    setExpandedPayment(null); // Collapse any expanded payment
  };

  // Function to handle payment row click
  const handlePaymentClick = (paymentId) => {
    // Toggle expanded state: if it's the same payment, collapse it; otherwise, expand the new one
    setExpandedPayment((prev) => (prev === paymentId ? null : paymentId));
  };

  //approve payment
  const [isOpen, setIsOpen] = useState(false);
  const closeModal = () => {
    setIsOpen(false);
  };

  const handleConfrim = () => {
    if (properties.length < 1) {
      return;
    }
    const paymentId = expandedPayment;
    dispatch(confirmPayments({ paymentId })).then((res) => {
      // Convert selected month to a number
      console.log("selected month", selectedMonth);
      let monthNumber;
      if (selectedMonth === "Select Month") {
        monthNumber = new Date().getMonth() + 1; // Current month (0-based index, so add 1)
      } else {
        const monthIndex = monthChoices.indexOf(selectedMonth);
        monthNumber = monthIndex !== -1 ? monthIndex + 1 : null;
        if (!monthNumber) {
          console.error("Invalid month selected");
          return;
        }
      }

      // Use the first property ID if selectedApartmentId is not provided
      const propertyId =
        selectedApartmentId ||
        (properties.length > 0 ? properties[0]._id : null);
      console.log("the property id", propertyId);
      if (!propertyId) {
        console.error("No property ID available");
        return;
      }
      setIsOpen(false);
      dispatch(
        fetchPayments({
          propertyId,
          month: monthNumber,
          year: selectedYear,
        })
      );
    });

    console.log("cleared");
  };

  //reverse payment
  const [isReverse, setIsReverse] = useState(false);
  const handleReverse = () => {
    alert("Payment Approved");
    // const paymentId = parseInt(expandedPayment);
    // dispatch(confirmPayments({ paymentId, token })).then((res) => {
    //   dispatch(
    //     fetchPayments({ property_id, token })
    //   );
    // });

    console.log("cleared");
  };

  return (
    <>
      {paymentsReceived?.length > 0 ? (
        <>
          <div className="table-container-pc-overview">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: "start" }}>House Number</th>
                  <th>Approval Status</th>
                  <th>Completion Status</th>
                  <th>Date</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {paymentData.payments
                  .slice(0, visiblePayments)
                  .map((payment, index) => (
                    <React.Fragment key={payment.payment_id || index}>
                      <tr
                        onClick={() => handlePaymentClick(payment.payment_id)}
                        className="clickable-row"
                      >
                        <td>{payment.house_no}</td>
                        <td
                          className={`dashboard-status ${
                            payment.approval_status === 0
                              ? "pending"
                              : "completed"
                          }`}
                        >
                          <div className="status-wrapper">
                            {payment.approval_status === 0
                              ? "Pending"
                              : "Completed"}
                          </div>
                        </td>
                        <td>
                          {/* {payment.payment_id_to_display} */}
                          <i
                            className={`dashboard-icon ${
                              payment.approval_status === 0
                                ? "dashboard-icon-pending"
                                : "dashboard-icon-completed"
                            }`}
                          >
                            {payment.approval_status === 0 ? (
                              <FaPercentage />
                            ) : (
                              <MdCreditScore />
                            )}
                          </i>
                        </td>
                        <td>
                          {new Date(payment.payment_date).toLocaleDateString()}
                        </td>
                        <td>{`ksh ${payment.amount}`}</td>
                      </tr>
                      {expandedPayment === payment.payment_id && (
                        <tr className="expanded-row">
                          <td colSpan="5">
                            <div className="expanded-content">
                              <div className="title">Transaction Details</div>
                              <div className="data">
                                <div>Invoice ID:</div>
                                <div className="data-details">
                                  {payment.invoice}
                                </div>
                              </div>
                              <div className="data">
                                <div>Payment ID:</div>
                                <div className="data-details">
                                  {payment.payment_id_to_display}
                                </div>
                              </div>
                              <div className="data">
                                <div>Rent :</div>
                                <div className="data-details">
                                  {payment.rent_amount}
                                </div>
                              </div>
                              <div className="data">
                                <div>Water :</div>
                                <div className="data-details">
                                  {payment.water_bill_amount}
                                </div>
                              </div>
                              <div className="data">
                                <div>Garbage :</div>
                                <div className="data-details">
                                  {payment.garbage_charge_amount}
                                </div>
                              </div>
                              <div className="data">
                                <div>Service Fee Amount:</div>
                                <div className="data-details">
                                  {payment.service_fee_amount}
                                </div>
                              </div>
                              <div className="data">
                                <div>Late Fee :</div>
                                <div className="data-details">
                                  {payment.late_fee}
                                </div>
                              </div>

                              <div className="data">
                                <div>Arrears :</div>
                                <div className="data-details">
                                  {payment.arrears_amount}
                                </div>
                              </div>
                              <div className="data">
                                <div>Deposit :</div>
                                <div className="data-details">
                                  {payment.unpaid_deposit}
                                </div>
                              </div>

                              <div className={`data final`}>
                                <div className="head">
                                  Total Payable Amount:
                                </div>
                                <div className="total">{payment?.amount}</div>
                              </div>

                              {payment.approval_status === 0 ? (
                                <>
                                  <div>
                                    {/* {rolesAssigned?.approvePayments && ( */}
                                    <>
                                      <button
                                        onClick={() => {
                                          setIsOpen(true);
                                        }}
                                      >
                                        Approve Payment
                                      </button>
                                    </>
                                    {/* )} */}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div>
                                    {/* {rolesAssigned?.approvePayments && ( */}
                                    <>
                                      <button
                                        className="reverse-payment"
                                        onClick={() => {
                                          setIsReverse(true);
                                          setIsOpen(true);
                                        }}
                                      >
                                        Reverse Payment
                                      </button>
                                    </>
                                    {/* )} */}
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>

            {/* Buttons to show more or hide payments */}
            <div className="button-container">
              {visiblePayments < paymentData.payments.length && (
                <button onClick={showMorePayments}>Show More</button>
              )}
              {visiblePayments > 3 && (
                <button onClick={hideAllPayments}>Hide All</button>
              )}
            </div>

            <PaymentConfirmationModal
              onReverse={handleReverse}
              isReverse={isReverse}
              isOpen={isOpen}
              paymentId={expandedPayment}
              onConfirm={handleConfrim}
              onCancel={closeModal}
            />
          </div>
        </>
      ) : (
        <>
          <div>No Payments Yet</div>
        </>
      )}
    </>
  );
};

export default TransactionsTable;
